import { unimportedItemLines } from "../../lib/optroImportJobs";
import { OptroImportItem } from "../../models/audit/optroSync";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

const MAX_LISTED = 5;

interface ImportOutcomeSummaryProps {
  items: OptroImportItem[];
  text: string;
}

// The import row's summary line. When items failed, timed out or were
// cancelled, hovering it lists them with the reason each one stopped, and screen
// readers get the same list as hidden text.
export const ImportOutcomeSummary = ({
  items,
  text,
}: ImportOutcomeSummaryProps) => {
  const lines = unimportedItemLines(items);
  if (lines.length === 0) {
    return <div className="mt-1 text-xs text-gray-600">{text}</div>;
  }

  const listed = lines.slice(0, MAX_LISTED);
  const screenReaderDetail = [
    ...listed.map((line) => `${line.name}: ${line.detail}`),
    ...(lines.length > MAX_LISTED
      ? [`and ${lines.length - MAX_LISTED} more`]
      : []),
  ].join("; ");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="mt-1 w-fit cursor-help text-xs text-gray-600">
          {text}
          {/* The tooltip is hover-only and its trigger sits inside the row
              button, so it can't take focus. Screen readers get the same
              reasons, bounded like the tooltip, as text instead. */}
          <span className="sr-only">. {screenReaderDetail}</span>
        </div>
      </TooltipTrigger>
      {/* The tooltip is portaled, but React events still bubble to the row
          button, so a click on it must not reopen the import's card. */}
      <TooltipContent
        side="left"
        className="max-w-xs"
        onClick={(event) => event.stopPropagation()}
      >
        <ul className="space-y-1 break-words">
          {listed.map((line) => (
            <li key={line.ref}>
              {line.name} — {line.detail}
            </li>
          ))}
          {lines.length > MAX_LISTED && (
            <li>+{lines.length - MAX_LISTED} more</li>
          )}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
};
